import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronsUpDown, X, Tractor } from "lucide-react";

const ModelSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/models`)
      .then((r) => setModels(r.data.models || []))
      .catch(() => setModels([]))
      .finally(() => setLoaded(true));
  }, []);

  const hasModels = models.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Tractor className="w-3.5 h-3.5" /> Model <span className="opacity-60 normal-case tracking-normal">(optional)</span>
        </label>
        {value && (
          <button
            data-testid="clear-model-btn"
            onClick={() => onChange(null)}
            className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-brand flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            data-testid="model-select"
            disabled={loaded && !hasModels}
            className="w-full justify-between font-normal h-11"
          >
            <span className={value ? "" : "text-muted-foreground"}>
              {value ? value : loaded && !hasModels ? "No models available yet" : "All Models"}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search model…" data-testid="model-search-input" />
            <CommandList>
              <CommandEmpty>No model found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__all__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                  All Models
                </CommandItem>
                {models.map((m) => (
                  <CommandItem
                    key={m.name}
                    value={m.name}
                    data-testid={`model-option-${m.name}`}
                    onSelect={() => {
                      onChange(m.name);
                      setOpen(false);
                    }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === m.name ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1">{m.name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{m.count}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ModelSelect;
